// Data seam for the Contactos area (unified contact/patient view). The pages
// mock THIS module in tests.

import { apiClient } from '../../api/client.js'

export interface ContactSummary {
  id: string
  displayName: string
  phone: string
  channel: string
  /** El nombre legible del canal ("WhatsApp"). Lo resuelve el servidor: el
   *  selector de contactos lo pone como segunda línea del renglón. */
  channelLabel: string
  tags: string[]
  lastSeen: string
}

export interface ContactConversation {
  id: string
  state: string
  startedAt: string
  lastMessageAt: string | null
  assignedAgentId: string | null
}

export interface ContactAppointment {
  id: string
  doctorName: string
  specialty: string
  startsAt: string
  endsAt: string
  status: string
  reason: string | null
}

export interface ContactLead {
  id: string
  name: string
  stage: string
  /** El PROCESO en el que vive la tarjeta, o null si su embudo ya no existe. */
  pipelineName: string | null
  /** El nombre RESUELTO de la etapa, con los nombres del proceso de la tarjeta
   *  (una etapa solo significa algo dentro de su embudo). */
  stageName: string
  /** Sigue en juego. `false` en cuanto cae en una etapa ganada o perdida. */
  open: boolean
  specialtyLabel: string | null
  appointmentAt: string | null
  createdAt: string
}

export type ContactLifecycleStage = 'lead' | 'customer' | 'churned'
export type ContactStatus = 'active' | 'do_not_contact' | 'opted_out' | 'bounced' | 'merged'

export interface ContactField {
  key: string
  label: string
  dataType: string
  options: string[] | null
  value: unknown
}

export interface ContactIdentity {
  channel: string
  externalId: string
  profileName: string | null
  verified: boolean
  firstSeen: string
}

export interface ContactDetail {
  contact: {
    id: string
    displayName: string
    profileName: string | null
    fullName: string | null
    phone: string
    email: string | null
    channel: string
    notes: string | null
    tags: string[]
    lifecycleStage: ContactLifecycleStage
    status: ContactStatus
    firstSeen: string
    lastSeen: string
  }
  fields: ContactField[]
  identities: ContactIdentity[]
  conversations: ContactConversation[]
  appointments: ContactAppointment[]
  leads: ContactLead[]
}

export interface ContactPatch {
  // null clears the field (empty input); undefined leaves it unchanged.
  fullName?: string | null
  /** El nombre CANÓNICO de la persona. A3: el modal mandaba `fullName`, pero
   *  `display_name` le gana en la cadena de respaldo y es justo donde vive el
   *  nombre de un contacto dado de alta a mano, así que renombrar no renombraba
   *  nada visible. */
  displayName?: string | null
  email?: string | null
  notes?: string | null
  tags?: string[]
  lifecycleStage?: ContactLifecycleStage
  status?: 'active' | 'do_not_contact'
  // Custom-field values keyed by field key; null clears one.
  fields?: Record<string, string | number | boolean | string[] | null>
}

/** GET /contacts — the tenant's contact list; `query` becomes the `q` search param. */
export async function getContacts(query?: string): Promise<ContactSummary[]> {
  const res = await apiClient.get<{ data: { items: ContactSummary[] } }>('/contacts', {
    params: query ? { q: query } : undefined,
  })
  return res.data.data.items
}

/** POST /contacts — alta MANUAL de una persona. El nombre es obligatorio; el
 *  teléfono no, porque a mucha gente se le captura antes de tener su número.
 *  Devuelve la persona creada, lista para vincular sin una segunda vuelta. */
export async function createContact(name: string, phone?: string): Promise<ContactSummary> {
  const res = await apiClient.post<{ data: ContactSummary }>('/contacts', {
    name,
    ...(phone ? { phone } : {}),
  })
  return res.data.data
}

/** GET /contacts/:id — full detail: custom fields, channel identities, conversations, citas and leads. */
export async function getContact(id: string): Promise<ContactDetail> {
  const res = await apiClient.get<{ data: ContactDetail }>(`/contacts/${id}`)
  return res.data.data
}

/** PATCH /contacts/:id — partial edit; in the patch, null clears a field and undefined leaves it unchanged. */
export async function updateContact(id: string, patch: ContactPatch): Promise<ContactSummary> {
  const res = await apiClient.patch<{ data: ContactSummary }>(`/contacts/${id}`, patch)
  return res.data.data
}

/** Fold `loserId` into `survivorId` (the survivor keeps its id + absorbs
 *  everything; the loser is tombstoned). */
export async function mergeContact(survivorId: string, loserId: string): Promise<void> {
  await apiClient.post(`/contacts/${encodeURIComponent(survivorId)}/merge`, { loserId })
}
